import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import { Heading } from '@acorns/web-components'
import { AnalyticsContextType, withAnalyticsClient } from '@acorns/web-utils'
import styled from 'styled-components'

import FullscreenTakeover from 'components/FullscreenTakeover'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { style } from 'theme'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import { StyledBody, StyledCard, StyledCloseAccountButton } from '../components'
import { EarlyClosureComplete, EarlyClosureInProgress } from './segment'

const BodyContainer = styled.div`
  padding-bottom: 50px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: auto;
    margin-top: 60px;
    align-self: center;
    padding-top: 50px;
  }
`
const ButtonContainer = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    padding-top: 20px;
    position: relative;
    width: 100%;
  }
`

const Footnote = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  font-size: 12px;
  font-weight: normal;
  height: 16px;
  letter-spacing: 0px;
  line-height: 16px;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 30px;
  padding-top: 50px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    margin-bottom: 0px;
    margin-top: 0;
  }
`
const StyledButton = styled(StyledCloseAccountButton)`
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin-top: 30px;
  }
`
// Height is calculated by subtracting Action Strip Height from Height of Window
const Card = styled(StyledCard)`
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin-top: 30px;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    display: flex;
    justify-content: space-between;
    padding-top: 20px;
    padding-bottom: 20px;
  }
`

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  display: flex;
  justify-content: space-between;
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
    padding: 0 30px 30px;
  }
`

const StyledHeading = styled(Heading)`
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  text-align: center;
  margin: 20px 0;
`

const ModifiedStyledBody = styled(StyledBody)`
  margin: 0 auto;
  margin-top: 0px;
`

const StyledIcon = styled(AcornsIcon)`
  height: 104px;
  width: 104px;
  margin: 0 auto;
  display: inline-block;
`

const StyledIconComplete = styled(AcornsIcon)`
  height: 104px;
  width: 104px;
  margin: 0 auto;
  display: inline-block;
`

const ClosureSuccess = ({ analytics }: AnalyticsContextType) => {
  const isWebView = window.location.pathname.startsWith('/wv/')
  const CardComponent = isWebView ? WVCard : Card
  const { height } = useWindowDimensions()
  const location = useLocation()
  const history = useHistory()
  const hasBalance = location?.state?.hasBalance
  const handleDoneClick = () => {
    const url = isWebView ? '/wv/my-subscription' : '/settings/my-subscription'
    history.push(url)
  }

  useEffect(() => {
    const event = location?.state?.hasBalance
      ? EarlyClosureInProgress
      : EarlyClosureComplete
    analytics.track('Screen Viewed', event)
  }, [])

  return (
    <>
      {isWebView && (
        <FullscreenTakeover innerHeight={height}>
          <CloseAccountWrapper
            isWebView={isWebView}
            action={{ shouldShowCloseIcon: true, onClick: handleDoneClick }}
          >
            {hasBalance ? (
              <CardComponent isWebView={isWebView} innerHeight={height}>
                <BodyContainer>
                  <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                  <StyledHeading>Closure in progress</StyledHeading>
                  <ModifiedStyledBody>
                    We'll start transferring your money and let you know as soon
                    as all accounts are closed.
                  </ModifiedStyledBody>
                </BodyContainer>
                <ButtonContainer>
                  <StyledCloseAccountButton onClick={handleDoneClick}>
                    Done
                  </StyledCloseAccountButton>
                </ButtonContainer>
              </CardComponent>
            ) : (
              <CardComponent isWebView={isWebView} innerHeight={height}>
                <BodyContainer>
                  <StyledIconComplete icon={AcornsIcon.Icon.AcornsCheckmark} />
                  <StyledHeading>Closure complete</StyledHeading>
                  <ModifiedStyledBody>
                    Your Early account is now closed.
                  </ModifiedStyledBody>
                </BodyContainer>
                <ButtonContainer>
                  <StyledCloseAccountButton onClick={handleDoneClick}>
                    Done
                  </StyledCloseAccountButton>
                </ButtonContainer>
              </CardComponent>
            )}
          </CloseAccountWrapper>
        </FullscreenTakeover>
      )}

      {!isWebView && (
        <>
          {hasBalance ? (
            <CardComponent>
              <BodyContainer>
                <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                <StyledHeading>Closure in progress</StyledHeading>
                <ModifiedStyledBody>
                  We'll start transferring your money and let you know as soon
                  as all accounts are closed.
                </ModifiedStyledBody>
              </BodyContainer>
              <ButtonContainer>
                <StyledButton onClick={handleDoneClick}>Done</StyledButton>
              </ButtonContainer>
            </CardComponent>
          ) : (
            <CardComponent>
              <BodyContainer>
                <StyledIconComplete icon={AcornsIcon.Icon.AcornsCheckmark} />
                <StyledHeading>Closure complete</StyledHeading>
                <ModifiedStyledBody>
                  Your Early account is now closed.
                </ModifiedStyledBody>
              </BodyContainer>
              <ButtonContainer>
                <StyledButton onClick={handleDoneClick}>Done</StyledButton>
              </ButtonContainer>
            </CardComponent>
          )}
        </>
      )}
    </>
  )
}

export default withAnalyticsClient(ClosureSuccess)
